import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, formatPrice, type ProductRow } from '../lib/api';

/**
 * The other direction: products the sheet has no row for.
 *
 * The preview answers "what does the sheet do to the app". This answers "what
 * is the sheet not saying anything about" — a gap that is invisible from the
 * sheet's own side, because a missing row looks exactly like a row that isn't
 * there for a good reason.
 *
 * Reuses the census the product table already runs on: `filter: 'no_sheet'`
 * server-side, resting on Product.sheetSeenAt stamped by every real pull. The
 * claim is only made relative to a run that read the whole sheet, so when there
 * has been no such run this says so rather than reporting a clean bill.
 */
export function SheetCoverage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const missing = useQuery({
    queryKey: ['products', search, 'no_sheet', 'active', 'all'],
    queryFn: () => api.products({ search, filter: 'no_sheet', limit: 500 }),
  });

  // One extra page of any products, purely to read the census state off it:
  // 'unknown' means no full pull has happened, which makes an empty missing
  // list meaningless rather than good news.
  const anyProduct = useQuery({
    queryKey: ['products', '', 'all', 'active', 'all'],
    queryFn: () => api.products({ limit: 1 }),
  });

  const censusUnknown = anyProduct.data?.products[0]?.sheetLink === 'unknown';

  if (missing.isLoading) return <div className="empty-state">Loading…</div>;
  if (missing.error) return <div className="alert error">{(missing.error as Error).message}</div>;

  const products = missing.data?.products ?? [];

  return (
    <>
      {censusUnknown && (
        <div className="alert warn small">
          No completed full pull on record yet, so nothing can be claimed either way. A preview does
          not count — it deliberately records no linkage. Run <strong>Pull now</strong> over the whole
          sheet (no SKU filter) and this list becomes meaningful.
        </div>
      )}

      <div className="toolbar">
        <input
          type="search"
          placeholder="SKU or product"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (e.target.value === '') setSearch('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput.trim());
          }}
        />
        <span className="small muted">
          {missing.data?.total ?? 0} product{(missing.data?.total ?? 0) === 1 ? '' : 's'} with no sheet row
        </span>
      </div>

      {products.length === 0 ? (
        <div className="empty-state">
          <p>{censusUnknown ? 'Nothing to show yet.' : 'Every product has a row in the sheet.'}</p>
          {!censusUnknown && (
            <p className="small">
              Measured against the last pull that read the whole sheet. A product added since then
              shows up here after the next one.
            </p>
          )}
        </div>
      ) : (
        <CoverageTable products={products} />
      )}
    </>
  );
}

function CoverageTable({ products }: { products: ProductRow[] }) {
  // Sold on both storefronts first: those are the ones where a missing sheet
  // row leaves the most listings unpriced.
  const sorted = useMemo(
    () => [...products].sort((a, b) => b.sites.length - a.sites.length || a.sku.localeCompare(b.sku)),
    [products],
  );

  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Product</th>
            <th style={{ width: 80 }}>Site</th>
            <th style={{ width: 90 }}>Listings</th>
            <th style={{ width: 110 }}>Website price</th>
            <th>Last seen in the sheet</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((product) => (
            <tr key={product.id}>
              <td className="sku">{product.sku}</td>
              <td className="title-cell">{product.title}</td>
              <td className="small">{product.sites.map((s) => s.toUpperCase()).join('+')}</td>
              <td className="small">{product.listings.length}</td>
              <td className="nowrap">{formatPrice(product.referencePriceMinor)}</td>
              <td className="small muted">
                {product.sheetSeenAt ? (
                  `${new Date(product.sheetSeenAt).toLocaleDateString()} — its row has gone since`
                ) : (
                  <span>never</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
